import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { DailyStats, CategoryRecord } from '../utils/statisticsManager';

/**
 * Statistikgrafer för StatisticsModal (alltid ljus yta, oberoende av speltema).
 *
 * Följer dataviz-metoden: färger validerade mot ljus yta med
 * validate_palette.js (#2a78d6/#eb6834 – alla checkar PASS), 2px linjer,
 * markörer ≥8px, tunna staplar med rundade ändar och 2px mellanrum, legend +
 * direktetiketter för två serier, hover-tooltip, text i textfärger (aldrig
 * seriefärg). Tabellvyn finns i modalens övriga flikar/listor.
 */

const SERIES_AVG = '#2a78d6'; // slot 1 (blå) – Snitt
const SERIES_BEST = '#eb6834'; // slot 2 (orange) – Bäst
const GRID = '#e5e5e5';
const TEXT_PRIMARY = '#333';
const TEXT_SECONDARY = '#666';

const ChartWrap = styled.div`
  position: relative;
  background: #fff;
  border-radius: 8px;
  padding: 8px 8px 4px;
`;

const Tooltip = styled.div`
  position: absolute;
  transform: translate(-50%, -100%);
  background: #fff;
  border: 1px solid ${GRID};
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  padding: 8px 10px;
  font-size: 12px;
  color: ${TEXT_PRIMARY};
  pointer-events: none;
  white-space: nowrap;
  z-index: 5;
`;

const LegendRow = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  font-size: 12px;
  color: ${TEXT_SECONDARY};
  padding: 4px 8px 8px;
`;

const Swatch = styled.span<{ $color: string }>`
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 3px;
  background: ${p => p.$color};
  margin-right: 6px;
  vertical-align: -1px;
`;

const EmptyNote = styled.div`
  color: ${TEXT_SECONDARY};
  font-size: 14px;
  padding: 24px 12px;
  text-align: center;
`;

const formatShortDate = (iso: string) =>
  new Date(iso).toLocaleDateString('sv-SE', { month: 'short', day: 'numeric' });

// --- Poängtrend (linjediagram, två serier) --------------------------------

const VIEW_W = 640;
const VIEW_H = 260;
const M = { top: 16, right: 74, bottom: 28, left: 40 }; // right rymmer direktetiketter

export const TrendLineChart: React.FC<{ days: DailyStats[] }> = ({ days }) => {
  const points = useMemo(() => days.filter(d => d.gamesPlayed > 0), [days]);
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);

  const geom = useMemo(() => {
    if (points.length === 0) return null;
    const innerW = VIEW_W - M.left - M.right;
    const innerH = VIEW_H - M.top - M.bottom;
    const maxScore = Math.max(...points.map(p => p.bestScore), 100);
    const yMax = Math.ceil(maxScore / 50) * 50; // snäpp till jämna 50
    const x = (i: number) =>
      M.left + (points.length === 1 ? innerW / 2 : (i / (points.length - 1)) * innerW);
    const y = (v: number) => M.top + innerH - (v / yMax) * innerH;
    return { innerW, innerH, yMax, x, y };
  }, [points]);

  if (!geom || points.length === 0) {
    return <EmptyNote>Spela några spel så visas din poängtrend här. 🎲</EmptyNote>;
  }

  const { yMax, x, y } = geom;
  const gridValues = [0, yMax / 2, yMax];
  const avgPath = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${x(i)},${y(p.averageScore)}`).join(' ');
  const bestPath = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${x(i)},${y(p.bestScore)}`).join(' ');
  const last = points.length - 1;
  const hovered = hoverIdx !== null ? points[hoverIdx] : null;

  const handleMove = (e: React.MouseEvent<SVGRectElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const relX = ((e.clientX - rect.left) / rect.width) * (VIEW_W - M.left - M.right) + M.left;
    let nearest = 0;
    let best = Infinity;
    points.forEach((_, i) => {
      const d = Math.abs(x(i) - relX);
      if (d < best) { best = d; nearest = i; }
    });
    setHoverIdx(nearest);
  };

  return (
    <>
      <ChartWrap>
        <svg viewBox={`0 0 ${VIEW_W} ${VIEW_H}`} width="100%" role="img"
             aria-label={`Poängtrend för ${points.length} speldagar. Bästa dag: ${Math.max(...points.map(p => p.bestScore))} poäng.`}>
          {/* Rutnät + y-etiketter (recessiva) */}
          {gridValues.map(v => (
            <g key={v}>
              <line x1={M.left} x2={VIEW_W - M.right} y1={y(v)} y2={y(v)} stroke={GRID} strokeWidth={1} />
              <text x={M.left - 8} y={y(v) + 4} textAnchor="end" fontSize={11} fill={TEXT_SECONDARY}>{v}</text>
            </g>
          ))}
          {/* X-etiketter: första + sista datum */}
          <text x={x(0)} y={VIEW_H - 8} textAnchor="start" fontSize={11} fill={TEXT_SECONDARY}>
            {formatShortDate(points[0].date)}
          </text>
          {points.length > 1 && (
            <text x={x(last)} y={VIEW_H - 8} textAnchor="end" fontSize={11} fill={TEXT_SECONDARY}>
              {formatShortDate(points[last].date)}
            </text>
          )}
          {/* Crosshair */}
          {hoverIdx !== null && (
            <line x1={x(hoverIdx)} x2={x(hoverIdx)} y1={M.top} y2={VIEW_H - M.bottom} stroke={TEXT_SECONDARY} strokeWidth={1} strokeDasharray="3 3" />
          )}
          {/* Serier: 2px linjer, markörer 8px (r=4) */}
          <path d={avgPath} fill="none" stroke={SERIES_AVG} strokeWidth={2} strokeLinejoin="round" />
          <path d={bestPath} fill="none" stroke={SERIES_BEST} strokeWidth={2} strokeLinejoin="round" />
          {points.map((p, i) => (
            <g key={p.date}>
              <circle cx={x(i)} cy={y(p.averageScore)} r={hoverIdx === i ? 5 : 4} fill={SERIES_AVG} stroke="#fff" strokeWidth={2} />
              <circle cx={x(i)} cy={y(p.bestScore)} r={hoverIdx === i ? 5 : 4} fill={SERIES_BEST} stroke="#fff" strokeWidth={2} />
            </g>
          ))}
          {/* Direktetiketter vid linjeslut (textfärg, inte seriefärg) */}
          <text x={x(last) + 10} y={y(points[last].averageScore) + 4} fontSize={12} fill={TEXT_PRIMARY}>Snitt</text>
          <text x={x(last) + 10} y={y(points[last].bestScore) + 4} fontSize={12} fill={TEXT_PRIMARY}>Bäst</text>
          {/* Hover-yta (större träffyta än markörerna) */}
          <rect x={M.left} y={M.top} width={VIEW_W - M.left - M.right} height={VIEW_H - M.top - M.bottom}
                fill="transparent" onMouseMove={handleMove} onMouseLeave={() => setHoverIdx(null)} />
        </svg>
        {hovered && hoverIdx !== null && (
          <Tooltip style={{ left: `${(x(hoverIdx) / VIEW_W) * 100}%`, top: `${(y(hovered.bestScore) / VIEW_H) * 100}%` }}>
            <strong>{formatShortDate(hovered.date)}</strong> · {hovered.gamesPlayed} spel<br />
            Bäst: {hovered.bestScore} p · Snitt: {Math.round(hovered.averageScore)} p
          </Tooltip>
        )}
      </ChartWrap>
      <LegendRow aria-hidden="true">
        <span><Swatch $color={SERIES_AVG} />Snitt per speldag</span>
        <span><Swatch $color={SERIES_BEST} />Bästa spel per dag</span>
      </LegendRow>
    </>
  );
};

// --- Kategorisnitt (liggande staplar, en serie) ---------------------------

/** Teoretiskt max per klassisk kategori – för "snitt som andel av max". */
const CATEGORY_MAX: Record<string, number> = {
  ones: 5, twos: 10, threes: 15, fours: 20, fives: 25, sixes: 30,
  onePair: 12, twoPairs: 22, threeOfAKind: 18, fourOfAKind: 24,
  smallStraight: 15, largeStraight: 20, fullHouse: 28, chance: 30, yatzy: 50,
};

const BAR_H = 14;
const BAR_GAP = 10; // ≥2px spacer-krav med god marginal
const BAR_LABEL_W = 92;
const BAR_VALUE_W = 96;

export const CategoryAvgBars: React.FC<{
  records: CategoryRecord[];
  getName: (key: string) => string;
}> = ({ records, getName }) => {
  const [hoverKey, setHoverKey] = useState<string | null>(null);
  const rows = useMemo(
    () => records
      .filter(r => CATEGORY_MAX[r.category] && r.timesUsed > 0)
      .map(r => ({ ...r, max: CATEGORY_MAX[r.category], pct: Math.min(1, r.averageScore / CATEGORY_MAX[r.category]) })),
    [records]
  );

  if (rows.length === 0) {
    return <EmptyNote>Inga kategoridata ännu – spela ett spel först. 🎲</EmptyNote>;
  }

  const width = 560;
  const height = rows.length * (BAR_H + BAR_GAP);
  const trackW = width - BAR_LABEL_W - BAR_VALUE_W;

  return (
    <ChartWrap>
      <svg viewBox={`0 0 ${width} ${height}`} width="100%" role="img"
           aria-label="Genomsnittspoäng per kategori som andel av max.">
        {rows.map((r, i) => {
          const yTop = i * (BAR_H + BAR_GAP);
          const w = Math.max(4, r.pct * trackW);
          const hovered = hoverKey === r.category;
          return (
            <g key={r.category}
               onMouseEnter={() => setHoverKey(r.category)}
               onMouseLeave={() => setHoverKey(null)}>
              {/* Träffyta över hela raden */}
              <rect x={0} y={yTop - BAR_GAP / 2} width={width} height={BAR_H + BAR_GAP} fill="transparent">
                <title>{`${getName(r.category)}: snitt ${r.averageScore} av max ${r.max} (${r.timesUsed} gånger)`}</title>
              </rect>
              <text x={BAR_LABEL_W - 8} y={yTop + BAR_H - 3} textAnchor="end" fontSize={12} fill={TEXT_PRIMARY}>
                {getName(r.category)}
              </text>
              {/* Spår + stapel med rundad data-ände */}
              <rect x={BAR_LABEL_W} y={yTop} width={trackW} height={BAR_H} rx={4} fill="#f1f3f5" />
              <rect x={BAR_LABEL_W} y={yTop} width={w} height={BAR_H} rx={4}
                    fill={SERIES_AVG} opacity={hovered ? 1 : 0.9} />
              <text x={BAR_LABEL_W + trackW + 8} y={yTop + BAR_H - 3} fontSize={12}
                    fill={hovered ? TEXT_PRIMARY : TEXT_SECONDARY}>
                {r.averageScore} / {r.max}
              </text>
            </g>
          );
        })}
      </svg>
    </ChartWrap>
  );
};
